import { useEffect, useState } from "react";
import type { NotificationsResponseDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { fetchAdminNotifications } from "../../shared/api/notification";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher", label: "講師ホームへ戻る" },
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" }
];

export function AdminHomePage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [notifications, setNotifications] = useState<NotificationsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadNotifications() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextNotifications = await fetchAdminNotifications(tenantSlug);

        if (cancelled) {
          return;
        }

        setNotifications(nextNotifications);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "admin notifications の取得に失敗しました");
      }
    }

    void loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, status, tenantSlug]);

  return (
    <PageScaffold
      title="Admin Home"
      description="管理者向けの tenant-wide operational alerts を表示します。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            admin 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock admin login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>admin notifications を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {hasAdminRole ? (
          <section style={{ display: "grid", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>運用アラート</h2>
            {notifications?.items.length === 0 ? <p style={{ margin: 0 }}>運用アラートはありません。</p> : null}
            {notifications?.items.map((item) => (
              <article
                key={item.itemId}
                style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 8 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <strong>{item.title}</strong>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>{item.priority}</span>
                </div>
                <div style={{ fontSize: 14 }}>{item.body}</div>
                {item.targetType && item.targetId ? (
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    {item.targetType} / {item.targetId}
                  </span>
                ) : null}
              </article>
            ))}
          </section>
        ) : null}
      </div>
    </PageScaffold>
  );
}

export { AdminEnrollmentsPage } from "./AdminEnrollmentsPage";
export { AdminEventsPage } from "./AdminEventsPage";
export { AdminGuardianLinkRequestsPage } from "./AdminGuardianLinkRequestsPage";
export { AdminGuardianLinksPage } from "./AdminGuardianLinksPage";
export { AdminIntegrationsPage } from "./AdminIntegrationsPage";
export { AdminLessonDetailPage } from "./AdminLessonDetailPage";
export { AdminLessonsPage } from "./AdminLessonsPage";
export { AdminMembersPage } from "./AdminMembersPage";
export { AdminOccurrenceDetailPage } from "./AdminOccurrenceDetailPage";
export { AdminPoliciesPage } from "./AdminPoliciesPage";
export { AdminRecurringSlotsPage } from "./AdminRecurringSlotsPage";
export { AdminScheduleMonthPage } from "./AdminScheduleMonthPage";
export { AdminStudentDetailPage } from "./AdminStudentDetailPage";
export { AdminStudentsPage } from "./AdminStudentsPage";
export { AdminVideosPage } from "./AdminVideosPage";
